import React, { useCallback, useState } from 'react';
import { Box, Select, TextField, Typography } from '@hiiretail/synergy-ui';
import {
  Controller,
  FieldValues,
  UseFormReturn,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { CommonMessages } from '../../../../../../common';
import { IdentityProvidersMessages } from '../../../../IdentityProviders.messages';
import { Option } from '@hiiretail/synergy-ui/dist/types/components/inputs/Select/Select.types';
import { promptOptions } from '../../../../utils/propmtOptions';
import { useIntl } from 'react-intl';
import { InlineHint } from '../../../../../../common/components/InlineHint/InlineHint';
import { ExternalIdpLogoutConfigForm } from 'pages/IdentityProviders/ui/IdentityProvidersList/components/ExternalIdpLogoutConfigForm/ExternalIdpLogoutConfigForm';
import { OidcFlowConfigForm } from '../../../IdentityProvidersList/components/OidcFlowConfigForm/OidcFlowConfigForm';

type CreateOidcProviderFormProps = {
  control: UseFormReturn['control'];
  formState: UseFormReturn['formState'];
  setValue: UseFormSetValue<FieldValues>;
  trigger: UseFormTrigger<FieldValues>;
  watch: UseFormWatch<FieldValues>;
};

type CreateOidcProviderFormComponent = React.FC<CreateOidcProviderFormProps>;

export const CreateOidcProviderForm: CreateOidcProviderFormComponent = ({
  control,
  formState,
  setValue,
  trigger,
  watch,
}) => {
  const intl = useIntl();
  const [showIdpLogoutConfig, setShowIdpLogoutConfig] = useState<boolean>(false);

  const handleAddIdpLogoutConfig = useCallback(() => {
    setShowIdpLogoutConfig((value) => !value);
    if (showIdpLogoutConfig) {
      setValue('externalIdpLogoutConfig', undefined);
    } else {
      setValue('externalIdpLogoutConfig.enabled', true);
      setValue('externalIdpLogoutConfig.redirectUrl', '');
    }
    trigger('externalIdpLogoutConfig', { shouldFocus: true });
  }, [setValue, showIdpLogoutConfig, trigger]);

  const selectedFlow = watch('flow');
  return (
    <form>
      <Box mb={3}>
        <Controller
          defaultValue=""
          name="name"
          control={control}
          render={({ field: { ref, value, onChange } }) => (
            <TextField
              tabIndex={0}
              required
              label={intl.formatMessage(CommonMessages.name)}
              name="name"
              fullWidth
              type="text"
              ref={ref}
              // @ts-ignore
              hint={
                <span>
                  Unique name for the identity provider
                  <br />
                  Must be between 3 and 256 characters long
                </span>
              }
              value={value}
              onChange={onChange}
              error={formState.errors?.name?.message as string | undefined}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <Controller
          name="externalIdField"
          control={control}
          defaultValue=""
          render={({ field: { value, onChange } }) => (
            <Select
              fullWidth
              name="externalIdField"
              required
              label={intl.formatMessage(CommonMessages.externalIdField)}
              isClearable={false}
              value={value?.value}
              isSearchable={false}
              // @ts-ignore
              hint={
                <span>
                  Specifies which field will be used by Hii Retail as a{' '}
                  <strong>unique user identifier</strong>,<br /> when importing users from the 3rd
                  party identity platform
                </span>
              }
              onChange={(option: Option | null) => onChange(option?.value)}
              placeholder={intl.formatMessage(IdentityProvidersMessages.selectExternalIdField)}
              error={formState.errors?.externalIdField?.message as string | undefined}
              options={[
                { label: intl.formatMessage(CommonMessages.email), value: 'Email' },
                { label: intl.formatMessage(CommonMessages.uid), value: 'Uid' },
                { label: intl.formatMessage(CommonMessages.phoneNumber), value: 'PhoneNumber' },
              ]}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <Controller
          control={control}
          name="userType"
          defaultValue=""
          render={({ field: { value, onChange } }) => (
            <Select
              fullWidth
              required
              label={intl.formatMessage(CommonMessages.userType)}
              value={value?.value}
              isClearable={false}
              error={formState.errors?.userType?.message as string | undefined}
              isSearchable={false}
              // @ts-ignore
              hint={
                <span>
                  Specifies which type of the user the identity provider will handle:
                  <br />
                  <strong>Staff</strong> users represent <strong>retail employees</strong>
                  <br />
                  <strong>Shoppers</strong> users represent <strong>retail customers</strong>
                  <br />
                </span>
              }
              name="userType"
              onChange={(option: Option | null) => onChange(option?.value)}
              placeholder={intl.formatMessage(IdentityProvidersMessages.selectUserType)}
              options={[
                { label: intl.formatMessage(CommonMessages.shoppers), value: 'Shoppers' },
                { label: intl.formatMessage(CommonMessages.staff), value: 'Staff' },
              ]}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <Controller
          name="clientId"
          defaultValue=""
          control={control}
          render={({ field: { ref, value, onChange } }) => (
            <TextField
              tabIndex={0}
              fullWidth
              required
              label={intl.formatMessage(CommonMessages.clientId)}
              name="clientId"
              hint="Can not be longer than 512 characters"
              type="text"
              ref={ref}
              value={value}
              onChange={onChange}
              error={formState.errors?.clientId?.message as string | undefined}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <Controller
          name="issuer"
          defaultValue=""
          control={control}
          render={({ field: { ref, value, onChange } }) => (
            <TextField
              fullWidth
              required
              label={intl.formatMessage(CommonMessages.issuer)}
              name="issuer"
              type="url"
              ref={ref}
              value={value}
              hint="Must be a valid URL"
              trailingIcon={
                <InlineHint>
                  <Typography variant="body2" weight="bold">
                    Issuer
                  </Typography>
                  <Typography variant="caption">
                    This should look something like <strong>https://example.com</strong>.<br />
                    Hii Retail uses this URL to locate the OIDC discovery document (typically found
                    at /.well-known/openid-configuration), which specifies the provider's OAuth
                    endpoints and public keys.
                  </Typography>
                </InlineHint>
              }
              onChange={onChange}
              error={formState.errors?.issuer?.message as string | undefined}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <Controller
          name="prompt"
          control={control}
          defaultValue={'select_account'}
          render={({ field: { value, onChange } }) => (
            <Select
              name="prompt"
              label={intl.formatMessage(CommonMessages.prompt)}
              value={value?.value}
              onChange={(option: Option | null) => onChange(option?.value)}
              error={formState.errors?.prompt?.message as string | undefined}
              placeholder={value}
              hint="Specifies whether the Authorization Server prompts the End-User for reauthentication and consent"
              isClearable={false}
              isSearchable={false}
              initialValue={{ label: 'Select account', value: 'select_account' }}
              fullWidth
              options={promptOptions}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <OidcFlowConfigForm
          idpFlow="create"
          control={control}
          formState={formState}
          selectedFlow={selectedFlow}
        />
      </Box>
      <Box mb={3}>
        <ExternalIdpLogoutConfigForm
          isOpen={showIdpLogoutConfig}
          handleAddForm={handleAddIdpLogoutConfig}
          control={control}
          formState={formState}
        />
      </Box>
      <Controller render={() => <span />} control={control} name="type" defaultValue="Oidc" />
    </form>
  );
};
